import { denormalisedResponseEntities } from '../../util/data';
import { storableError, isTooManyRequestsError } from '../../util/errors';
import { scanAndUploadImageAPI } from '../../util/api';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const FETCH_INQUIRED_SUCCESS = 'app/ProfileSettingsPage/FETCH_INQUIRED_SUCCESS';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  inquiredListings: [],
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage, contactPair }
      const { id, uploadedImage, contactPair } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage, contactPair };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case FETCH_INQUIRED_SUCCESS:
      return { ...state, inquiredListings: payload || [] };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const updateProfileInProgress = state => {
  return state.ProfileSettingsPage.updateInProgress;
};

export const fetchInquiredSuccess = listings => ({
  type: FETCH_INQUIRED_SUCCESS,
  payload: listings,
});

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id.
//
// HAUL STAR IMAGE PROTECTION: route the raw profile image through our server
// endpoint so baked-in contact info (e.g. a business logo with a phone number)
// is detected and redacted for free owners before it lands on Sharetribe. The
// endpoint returns the image to attach now (redacted for free / clean for Haul
// Star) plus the { cleanId, redactedId } pair, which we stash so it gets written
// to the user's protectedData at profile-save time (enabling clean<->redacted
// swap on membership change).
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    return scanAndUploadImageAPI(actionPayload.file)
      .then(resp => {
        const uploadedImage = resp.activeImage;
        if (!uploadedImage || !uploadedImage.id) {
          throw new Error('Image upload returned no image');
        }
        dispatch(
          uploadImageSuccess({ data: { id, uploadedImage, contactPair: resp.pair || null } })
        );
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

// Fetch listings the current (renter) user has inquired about, via their own
// transactions. Best-effort: any failure just leaves the list empty so the
// profile page never breaks. Renter-only usage.
export const fetchInquiredListings = () => {
  return (dispatch, getState, sdk) => {
    return sdk.transactions
      .query({
        only: 'order',
        // BUGFIX (PL5): without lastTransitions the marketplace API does NOT
        // return inquiry-without-payment transactions, so the renter's
        // "Trailers you've inquired about" list came back empty even after
        // they messaged an owner. Mirror the working InboxPage query.
        lastTransitions: getAllTransitionsForEveryProcess(),
        include: ['listing', 'listing.images'],
        'fields.listing': ['title', 'price', 'publicData'],
        'fields.image': ['variants.landscape-crop', 'variants.square-small2x'],
        perPage: 50,
      })
      .then(res => {
        const included = res.data.included || [];
        const images = {};
        included
          .filter(e => e.type === 'image')
          .forEach(img => {
            const v = img.attributes?.variants || {};
            const variant = v['landscape-crop'] || v['square-small2x'];
            if (variant) images[img.id.uuid] = variant.url;
          });

        const listings = included.filter(e => e.type === 'listing');
        // de-dupe listings (a renter may have multiple tx on the same listing)
        const seen = new Set();
        const out = [];
        listings.forEach(l => {
          const id = l.id.uuid;
          if (seen.has(id)) return;
          seen.add(id);
          const pub = l.attributes?.publicData || {};
          const priceAmt = l.attributes?.price?.amount;
          const imgRel = l.relationships?.images?.data?.[0];
          const image = imgRel ? images[imgRel.id.uuid] : null;
          const cityState = [pub.city, pub.state].filter(Boolean).join(', ');
          out.push({
            id,
            slug: 'trailer',
            title: l.attributes?.title || 'Trailer',
            meta: cityState || '',
            price: typeof priceAmt === 'number' ? `$${Math.round(priceAmt / 100)}/day` : '',
            image,
          });
        });
        dispatch(fetchInquiredSuccess(out.slice(0, 12)));
        return out;
      })
      .catch(() => {
        dispatch(fetchInquiredSuccess([]));
      });
  };
};

export const loadData = () => dispatch => {
  return dispatch(fetchInquiredListings());
};

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    // Haul Star image protection: if a new profile image was just uploaded and it
    // contained baked-in contact info, persist its { cleanId, redactedId } pair
    // into protectedData so subscriptionSync can swap the avatar clean<->redacted
    // on membership change. Merge with existing protectedData (updateProfile
    // replaces the object wholesale).
    let finalPayload = actionPayload;
    const stagedImage = getState().ProfileSettingsPage.image;
    const cp = stagedImage?.contactPair;
    // Only stamp when THIS save includes the newly-uploaded image.
    const includesNewImage =
      actionPayload?.profileImageId &&
      stagedImage?.imageId &&
      (actionPayload.profileImageId.uuid || actionPayload.profileImageId) ===
        (stagedImage.imageId.uuid || stagedImage.imageId);
    if (includesNewImage && cp && cp.cleanId && cp.redactedId) {
      const currentUser = getState().user.currentUser;
      const existingProtected = currentUser?.attributes?.profile?.protectedData || {};
      finalPayload = {
        ...actionPayload,
        protectedData: {
          ...existingProtected,
          ...(actionPayload.protectedData || {}),
          profileImageContactPair: { cleanId: cp.cleanId, redactedId: cp.redactedId || null },
        },
      };
    }

    // Retry-with-backoff on HTTP 429. Sharetribe Live environments are not
    // rate-limited by request volume, but there is a hard 10-concurrent-
    // requests-per-IP cap across ALL environments. On a heavy page load a burst
    // of background reads (notifications, inbox-unread scan, hasListings, etc.)
    // can momentarily cross that cap; if the user's Save POST loses that race it
    // comes back 429 and the save appears to "fail" even though nothing is wrong
    // with the payload. Sharetribe explicitly recommends retrying 429s with
    // backoff — by the time we retry, the burst has drained and the save lands.
    // The global SDK concurrency limiter (sdkLoader.js, p-limit 6) is the primary
    // 429 fix — it keeps a single tab under Sharetribe's 10-concurrent-per-IP cap
    // so the save has headroom. This retry is a secondary safety net for the
    // cross-tab / same-IP (NAT) case, where slow background queries in ANOTHER
    // tab can still hold slots for several seconds. Delays are LONG (2s, 5s) so a
    // retry lands AFTER those heavy queries drain, not inside the occupancy
    // window (the old 0.6/1.4/2.8s schedule fired uselessly inside it).
    const sleep = ms => new Promise(r => setTimeout(r, ms));
    const RETRY_DELAYS = [2000, 5000];
    const updateWithRetry = (attempt = 0) =>
      sdk.currentUser.updateProfile(finalPayload, queryParams).catch(err => {
        if (isTooManyRequestsError(err) && attempt < RETRY_DELAYS.length) {
          const backoff = RETRY_DELAYS[attempt] + Math.floor(Math.random() * 500);
          return sleep(backoff).then(() => updateWithRetry(attempt + 1));
        }
        throw err;
      });

    return updateWithRetry()
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
        return currentUser;
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};
